<template>
  <b-button
    v-if="isAIFeaturesEnabled"
    class="create-content-btn"
    size="sm"
    :disabled="disabled"
    @click="handleClick"
    v-b-tooltip.hover
    title="🤖 AI สร้างเนื้อหาสำหรับ LINE และ Facebook ในไม่กี่วินาที!"
  >
    <i class="fas fa-magic"></i>
    <span>Smart Content</span>
  </b-button>
</template>

<script>
import { isAIFeaturesEnabled } from '@/utils/environmentCheck';

export default {
  name: 'CreateContentButton',
  
  props: {
    recommendation: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    isAIFeaturesEnabled() {
      return isAIFeaturesEnabled();
    }
  },
  
  methods: {
    handleClick() {
      if (!this.disabled) {
        this.$emit('click', this.recommendation);
      }
    }
  }
};
</script>

<style scoped>
/* Innovative Create Content Button - AI-Powered Design */
.create-content-btn {
  padding: 8px 16px !important;
  min-width: 140px;
  border-radius: 25px;
  border: 2px solid transparent !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%) !important;
  background-size: 300% 300%;
  color: white !important;
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 
    0 4px 15px rgba(102, 126, 234, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  position: relative;
  overflow: hidden;
  animation: gradientShift 3s ease infinite;
}

/* Animated gradient background */
@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* Glowing border effect */
.create-content-btn::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #feca57, #ff9ff3);
  background-size: 400% 400%;
  border-radius: 25px;
  z-index: -1;
  animation: borderGlow 2s linear infinite;
  opacity: 0;
  transition: opacity 0.3s ease;
}

@keyframes borderGlow {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* Shimmer effect */
.create-content-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  transition: left 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hover effects */
.create-content-btn:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 
    0 8px 25px rgba(102, 126, 234, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.2) inset;
  color: white !important;
}

.create-content-btn:hover::before {
  opacity: 1;
}

.create-content-btn:hover::after {
  left: 100%;
}

/* Active/Click effect */
.create-content-btn:active {
  transform: translateY(-1px) scale(0.98);
  transition: all 0.1s ease;
}

/* Focus state */
.create-content-btn:focus {
  outline: none;
  box-shadow: 
    0 4px 15px rgba(102, 126, 234, 0.4),
    0 0 0 3px rgba(102, 126, 234, 0.3) !important;
  color: white !important;
}

/* Disabled state */
.create-content-btn:disabled {
  background: linear-gradient(135deg, #9ca3af, #6b7280) !important;
  color: #d1d5db !important;
  transform: none;
  box-shadow: none;
  cursor: not-allowed;
  animation: none;
}

.create-content-btn:disabled::before,
.create-content-btn:disabled::after {
  display: none;
}

/* Icon styling with pulse animation */
.create-content-btn i {
  font-size: 0.85rem;
  margin: 0;
  animation: iconPulse 2s ease-in-out infinite;
}

@keyframes iconPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.create-content-btn span {
  font-weight: 700;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Responsive design */
@media (max-width: 768px) {
  .create-content-btn {
    padding: 6px 12px !important;
    min-width: 110px;
    font-size: 0.75rem;
    letter-spacing: 0.3px;
  }
  
  .create-content-btn i {
    font-size: 0.8rem;
  }
}

@media (max-width: 576px) {
  .create-content-btn {
    padding: 6px 10px !important;
    min-width: 45px;
    border-radius: 12px;
    gap: 0;
  }
  
  .create-content-btn span {
    display: none;
  }
  
  .create-content-btn i {
    font-size: 0.9rem;
    margin: 0 !important;
    animation: none; /* Disable pulse on mobile for performance */
  }
  
  .create-content-btn::before {
    border-radius: 12px;
  }
}

/* Special AI-powered indicator */
.create-content-btn:not(:disabled):hover i {
  animation: iconSpark 0.6s ease-in-out;
}

@keyframes iconSpark {
  0%, 100% { transform: scale(1) rotate(0deg); }
  25% { transform: scale(1.2) rotate(-5deg); }
  75% { transform: scale(1.2) rotate(5deg); }
}
</style>